import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FiAlertCircle, FiArrowRight, FiCheck, FiCheckCircle, FiChevronDown,
  FiHelpCircle, FiMail, FiRefreshCw, FiSearch, FiShield, FiZap
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const field = 'w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';
const validEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
const websiteOrigin = value => {
  try {
    const url = new URL(String(value || '').trim());
    if (url.protocol !== 'https:' || url.username || url.password) return '';
    return url.origin.toLowerCase();
  } catch { return ''; }
};
const knownAssessmentOrigin = 'https://assessments.bpasolutions.au';

async function listAll(fetchPage) {
  const rows = [];
  for (let page = 1; page <= 30; page++) {
    const response = await fetchPage(page);
    rows.push(...(response.data?.items || []));
    if (!response.data?.hasMore) return rows;
  }
  throw new Error('Your Gateway has more records than Quick Connect can display. Open Advanced setup for targeted configuration.');
}
function TextField({ label, value, onChange, placeholder, help, type = 'text' }) {
  return <label className="block min-w-0 space-y-1.5">
    <span className="block text-xs font-semibold text-slate-700">{label}</span>
    <input className={field} value={value} type={type} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    {help && <span className="block text-xs leading-5 text-slate-500">{help}</span>}
  </label>;
}
const friendlyCheck = {
  'Active business':'Business is ready',
  'Active application':'Application is ready',
  'Active private API key':'Secure connection is ready',
  'Emailit configured':'Email service is ready',
  'Sender domain approved':'Sending address is ready',
  'Worker healthy':'Email delivery system is running',
  'Test recipient authorised':'Test inbox is allowed'
};
function StatusLine({ ok, title }) {
  return <div className="flex items-start gap-2 text-sm">
    {ok ? <FiCheckCircle className="mt-0.5 shrink-0 text-emerald-600"/> : <FiAlertCircle className="mt-0.5 shrink-0 text-amber-600"/>}
    <span className={ok ? 'text-slate-700' : 'text-amber-900'}>{title}</span>
  </div>;
}

function WorkflowRules({ appId, clientId }) {
  const [routes,setRoutes]=useState([]);
  const [templates,setTemplates]=useState([]);
  const [eventKey,setEventKey]=useState('');
  const [templateKey,setTemplateKey]=useState('');
  const [status,setStatus]=useState('');
  const [saving,setSaving]=useState(false);
  useEffect(()=>{
    if(!appId)return;
    let live=true;
    Promise.all([gatewayApi.listWorkflowRoutes(appId),listAll(gatewayApi.listTemplatesPage)])
      .then(([data,all])=>{
        if(!live)return;
        setRoutes(data.data?.routes||[]);
        const matched=all.filter(t=>t.status==='active' && (t.app_id===appId || (!t.app_id && (!t.client_id || t.client_id===clientId))));
        setTemplates(matched);
        setTemplateKey(matched[0]?.template_key||'');
      }).catch(err=>{if(live)setStatus(err.message||'Could not read email event rules.');});
    return ()=>{live=false;};
  },[appId,clientId]);
  const save=async(enabled=true,selected=eventKey)=>{
    const value=String(selected||'').trim().toLowerCase();
    if(!/^[a-z][a-z0-9-]{1,59}$/.test(value)){
      setStatus('Use a short event name such as invoice-ready or booking-confirmed.');return;
    }
    if(enabled&&!templateKey){setStatus('Choose an email template first.');return;}
    setSaving(true);setStatus('');
    try{
      const response=await gatewayApi.saveWorkflowRoute({appId,eventKey:value,templateKey,enabled});
      setRoutes(response.data?.routes||[]);
      setStatus(enabled?'Email event saved. The app backend can now trigger this named email.':'Email event removed.');
      if(enabled)setEventKey('');
    }catch(err){setStatus(err.message||'Could not save the event.');}
    finally{setSaving(false);}
  };
  return <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
    <h3 className="font-semibold text-slate-800">Add an email event without changing server code</h3>
    <p>Examples: booking-confirmed, invoice-ready, case-note-updated. Each event uses an approved template. Your application backend still needs to trigger the event securely.</p>
    <input className={field} value={eventKey} onChange={e=>setEventKey(e.target.value)} placeholder="Event name, e.g. booking-confirmed" aria-label="Email event name"/>
    <select className={field} value={templateKey} onChange={e=>setTemplateKey(e.target.value)} aria-label="Email template">
      {!templates.length&&<option value="">Create an active template first</option>}
      {templates.map(t=><option key={t.id} value={t.template_key}>{t.template_name||t.template_key}</option>)}
    </select>
    <button type="button" disabled={saving||!templates.length} onClick={()=>save()} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Save email event</button>
    {routes.map(r=><div key={r.eventKey} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white p-2 text-xs">
      <span><strong>{r.eventKey}</strong> · {r.templateKey}</span>
      <button type="button" className="font-semibold text-rose-700 underline" disabled={saving} onClick={()=>{if(window.confirm('Remove this email event?'))save(false,r.eventKey);}}>Remove</button>
    </div>)}
    {status&&<p role="status" className="text-xs text-blue-800">{status}</p>}
    <Link to="/templates" className="block font-semibold text-blue-700 underline">Manage reusable email templates</Link>
  </div>;
}
export default function SimpleConnect() {
  const [params] = useSearchParams();
  const preferredApp = params.get('app') || '';
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [mode, setMode] = useState('existing');
  const [search, setSearch] = useState('');
  const [appId, setAppId] = useState(preferredApp);
  const [businessId, setBusinessId] = useState('');
  const [newBusiness, setNewBusiness] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [newApp, setNewApp] = useState('');
  const [website, setWebsite] = useState('');
  const [purpose, setPurpose] = useState('general');
  const [testEmail, setTestEmail] = useState('');
  const [working, setWorking] = useState(false);
  const [phase, setPhase] = useState('');
  const [connected, setConnected] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [needsTest, setNeedsTest] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(true);
  const [allowCustomerRecipients, setAllowCustomerRecipients] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const [foundClients, foundApps] = await Promise.all([
          listAll(gatewayApi.listClientsPage),
          listAll(gatewayApi.listAppsPage)
        ]);
        if (!active) return;
        setClients(foundClients);
        setApps(foundApps);
        const initial = foundApps.find(a => a.id === preferredApp);
        if (initial) {
          const client = foundClients.find(c => c.id === initial.clientId);
          setAppId(initial.id);
          setWebsite(initial.origins?.split(',').map(x=>x.trim()).find(x=>x.startsWith('https://')) || '');
          setTestEmail(client?.contactEmail || '');
          setPurpose((initial.origins || '').includes(knownAssessmentOrigin) ? 'assessment' : 'general');
        } else if (foundApps.length === 0) {
          setMode('new');
          if (foundClients.length === 1) setBusinessId(foundClients[0].id);
        }
      } catch (err) {
        if (active) setProblem(err.message || 'Unable to find your applications. Check the Gateway connection.');
      } finally { if (active) setLoading(false); }
    }
    load();
    gatewayApi.quickConnectPreflight().then(result => { if (active) setPreflight(result.data); })
      .catch(err => { if (active) setPreflight({ready:false,checkError:err.message}); })
      .finally(() => { if (active) setPreflightLoading(false); });
    return () => { active = false; };
  }, [preferredApp]);

  const selectedApp = apps.find(app => app.id === appId);
  const owner = clients.find(client => client.id === selectedApp?.clientId);
  const selectedBusiness = clients.find(client => client.id === businessId);
  const businessName = mode === 'existing' ? owner?.name : selectedBusiness?.name || newBusiness;
  const businessEmail = mode === 'existing' ? owner?.contactEmail : selectedBusiness?.contactEmail || contactEmail;
  const displayApp = mode === 'existing' ? selectedApp?.name : newApp;
  const origin = websiteOrigin(website);
  const destination = testEmail.trim() || businessEmail || '';
  const assessmentSupported = purpose !== 'assessment' || origin === knownAssessmentOrigin;
  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    return apps.filter(app => [app.name, app.origins, clients.find(c=>c.id===app.clientId)?.name]
      .some(value => String(value||'').toLowerCase().includes(term)))
      .sort((a,b) => a.name.localeCompare(b.name));
  }, [apps, clients, search]);
  const duplicate = mode === 'new' ? apps.find(app => app.name.toLowerCase() === newApp.trim().toLowerCase()
      || (origin && (app.origins||'').split(',').some(x => x.trim() === origin))) : null;
  const valid = Boolean(
    displayApp?.trim() && businessName?.trim() && validEmail(businessEmail) &&
    origin && validEmail(destination) && assessmentSupported &&
    (mode === 'existing' ? selectedApp?.id : newApp.trim()) && !duplicate
  );
  const resetResult = () => {
    setConnected(null); setDiagnostics(null); setTestResult(null); setNeedsTest(false);
    setProblem(''); setPhase('');
  };
  const selectApp = app => {
    if (working) return;
    const client = clients.find(c => c.id === app.clientId);
    setAppId(app.id);
    const url = app.origins?.split(',').map(x=>x.trim()).find(x=>x.startsWith('https://')) || '';
    setWebsite(url);
    setPurpose(url === knownAssessmentOrigin ? 'assessment' : 'general');
    setTestEmail(client?.contactEmail || '');
    resetResult();
  };
  const changeMode = value => {
    if (working) return;
    setMode(value); resetResult();
    if (value === 'new') {
      setWebsite(''); setTestEmail(''); setPurpose('general');
      if (!businessId && clients.length === 1) setBusinessId(clients[0].id);
    } else if (selectedApp) selectApp(selectedApp);
  };
  const runChecks = async (appIdToCheck, to, registered = null) => {
    setPhase('Checking the connection…');
    const response = await gatewayApi.diagnoseOnboarding(appIdToCheck, to);
    const checks = response.data;
    setDiagnostics(checks);
    if (!checks.ready) {
      setNeedsTest(true);
      setPhase('A setup check needs attention. No test email was sent.');
      return;
    }
    setPhase('Sending one test email to your inbox…');
    const sent = await gatewayApi.testProvider(to, appIdToCheck, true);
    setTestResult(sent.data);
    setNeedsTest(false);
    setPhase(sent.data?.providerAccepted
      ? 'Your test email was accepted for sending. Check your inbox to confirm arrival.'
      : 'The test is pending or could not be accepted. Check Delivery Queue.');
    if (registered) setConnected(registered);
  };
  const connectAndTest = async () => {
    if (!valid || working || preflightLoading || preflight?.ready===false) return;
    setWorking(true); setProblem(''); setConnected(null); setDiagnostics(null); setTestResult(null); setNeedsTest(false);
    setPhase('Preparing your application and secure email settings…');
    let created = null;
    try {
      const payload = {
        existingClientId: mode === 'existing' ? owner.id : selectedBusiness?.id || '',
        existingAppId: mode === 'existing' ? selectedApp.id : '',
        businessName: businessName.trim(),
        contactEmail: businessEmail.trim(),
        productName: mode === 'existing' ? selectedApp.name : newApp.trim(),
        testRecipient: destination.trim(),
        allowAnyRecipient: purpose==='general' && allowCustomerRecipients,
        monthlyLimit: 1000, rateLimitPerMinute: 10,
        quickOrigin: origin,
        quickWorkflow: purpose === 'assessment' ? 'verified_assessment' : 'standard',
        quickNotificationEmail: purpose === 'assessment' ? destination.trim() : ''
      };
      const response = await gatewayApi.quickConnectApp(payload);
      created = response.data;
      setConnected(created);
      // An application must never be called working just because records
      // were created or a new private key was generated.
      await runChecks(created.app.id, destination.trim(), created);
    } catch (err) {
      setProblem(err.message || 'The connection could not be completed.');
      setPhase(created ? 'Application saved, but the test needs attention.' : 'No verified connection yet.');
      setNeedsTest(Boolean(created));
    } finally { setWorking(false); }
  };
  const retryTest = async () => {
    if (working || !connected) return;
    setWorking(true); setProblem(''); setTestResult(null);
    try { await runChecks(connected.app.id, destination.trim()); }
    catch (err) {
      setProblem(err.message || 'The test did not complete. Please try again.');
      setNeedsTest(true); setPhase('Your application is registered. The test is not complete.');
    } finally { setWorking(false); }
  };
  const fullSuccess = Boolean(testResult?.providerAccepted);
  const hasRegistered = Boolean(connected?.app?.id);

  return <div className="mx-auto max-w-5xl space-y-5 pb-12">
    <header className="rounded-3xl bg-slate-900 px-5 py-6 text-white sm:px-8">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-blue-200"><FiZap/> BPA Solutions · Email Gateway</div>
      <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Connect an app</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-300">Everything on one screen. Pick an app, choose the type of email, then select Connect &amp; Test. The Gateway handles the technical setup.</p>
    </header>

    {problem && <div role="alert" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"><FiAlertCircle className="mt-0.5 shrink-0"/>{problem}</div>}
    {!preflightLoading && preflight?.ready===false && <div role="alert" className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"><strong>Connect & Test is not ready yet.</strong><p>{preflight.checkError || 'Your Email Hub needs the following server-side configuration:'}</p>{(preflight.missingSettings||[]).length>0 && <p><strong>Missing PocketHost Secrets:</strong> {preflight.missingSettings.join(', ')}</p>}{(preflight.missingCollections||[]).length>0 && <p><strong>Missing Email Hub collections:</strong> {preflight.missingCollections.join(', ')}</p>}<p>Set these on your existing Email Hub PocketHost instance. Do not enter secret values into this form.</p><button type="button" className="rounded-lg border border-amber-500 bg-white px-3 py-2 font-semibold" onClick={async()=>{setPreflightLoading(true);try{setPreflight((await gatewayApi.quickConnectPreflight()).data);}catch(err){setPreflight({ready:false,checkError:err.message});}finally{setPreflightLoading(false);}}}>Recheck connection</button></div>}
    <section className="grid items-start gap-5 lg:grid-cols-5">
      <div className="space-y-5 lg:col-span-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div><h2 className="text-base font-bold text-slate-900">1. Find your app</h2><p className="mt-1 text-xs text-slate-500">Select one you already have, or add a new one.</p></div>
            <FiSearch className="mt-1 text-blue-600"/>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" disabled={working} aria-pressed={mode==='existing'} onClick={()=>changeMode('existing')} className={'rounded-xl border p-3 text-sm font-semibold disabled:opacity-50 '+(mode==='existing'?'border-blue-500 bg-blue-50 text-blue-800':'border-slate-200 text-slate-600')}>Find existing</button>
            <button type="button" disabled={working} aria-pressed={mode==='new'} onClick={()=>changeMode('new')} className={'rounded-xl border p-3 text-sm font-semibold disabled:opacity-50 '+(mode==='new'?'border-blue-500 bg-blue-50 text-blue-800':'border-slate-200 text-slate-600')}>Add new</button>
          </div>
          {loading ? <p className="mt-4 text-sm text-slate-500"><FiRefreshCw className="mr-2 inline animate-spin"/>Finding your registered apps…</p>
          : mode==='existing' ? <div className="mt-4 space-y-3">
            <TextField label="Search for an app" value={search} onChange={setSearch} placeholder="App, business or website"/>
            <div className="max-h-52 space-y-1.5 overflow-y-auto">
              {matches.map(app=><button key={app.id} type="button" disabled={working} onClick={()=>selectApp(app)}
                aria-pressed={selectedApp?.id===app.id}
                className={'flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left disabled:opacity-50 '+(selectedApp?.id===app.id?'border-blue-500 bg-blue-50':'border-slate-200 hover:bg-slate-50')}>
                <span className="min-w-0"><strong className="block truncate text-sm text-slate-800">{app.name}</strong><span className="block truncate text-xs text-slate-500">{clients.find(c=>c.id===app.clientId)?.name||'Unknown business'}</span></span>
                {selectedApp?.id===app.id ? <FiCheckCircle className="shrink-0 text-blue-600"/> : <FiArrowRight className="shrink-0 text-slate-400"/>}
              </button>)}
              {!matches.length && <p className="p-3 text-xs text-slate-500">No app found. Choose Add new above.</p>}
            </div>
            {owner && <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600"><FiCheck className="mr-1 inline text-emerald-600"/> Business details automatically found for <strong>{owner.name}</strong>.</div>}
          </div> : <div className="mt-4 space-y-3">
            <TextField label="App name" value={newApp} onChange={value=>{setNewApp(value);resetResult();}} placeholder="e.g. HavenFlow"/>
            <label className="block space-y-1.5"><span className="block text-xs font-semibold text-slate-700">Business</span>
              <select className={field} value={businessId} onChange={e=>{setBusinessId(e.target.value);resetResult();}} disabled={working}>
                <option value="">Add a new business (only if BPA Solutions is not listed)</option>
                {clients.map(client=><option key={client.id} value={client.id}>{client.name}</option>)}
              </select>
            </label>
            {!selectedBusiness && <div className="grid gap-3 sm:grid-cols-2">
              <TextField label="Business name" value={newBusiness} onChange={value=>{setNewBusiness(value);resetResult();}} placeholder="Business name"/>
              <TextField label="Business contact email" type="email" value={contactEmail} onChange={value=>{setContactEmail(value);resetResult();}} placeholder="team@business.com"/>
            </div>}
            {selectedBusiness && <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">Using {selectedBusiness.name} and its existing contact email.</p>}
            {duplicate && <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900">This app or website is already registered as <strong>{duplicate.name}</strong>. <button className="font-bold underline" type="button" onClick={()=>{setMode('existing');selectApp(duplicate);}}>Use existing app</button></div>}
          </div>}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-base font-bold text-slate-900">2. What should it send?</h2>
          <p className="mt-1 text-xs text-slate-500">The provider and security settings are configured automatically.</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <button type="button" disabled={working} aria-pressed={purpose==='assessment'} onClick={()=>{setPurpose('assessment');resetResult();}}
              className={'rounded-xl border p-3 text-left disabled:opacity-50 '+(purpose==='assessment'?'border-blue-500 bg-blue-50':'border-slate-200 hover:bg-slate-50')}>
              <FiShield className="mb-2 text-blue-600"/><strong className="block text-sm text-slate-800">Verified assessment PDF</strong><span className="mt-1 block text-xs leading-5 text-slate-500">For your existing Assessment Hub OTP and report emails.</span>
            </button>
            <button type="button" disabled={working} aria-pressed={purpose==='general'} onClick={()=>{setPurpose('general');resetResult();}}
              className={'rounded-xl border p-3 text-left disabled:opacity-50 '+(purpose==='general'?'border-blue-500 bg-blue-50':'border-slate-200 hover:bg-slate-50')}>
              <FiMail className="mb-2 text-blue-600"/><strong className="block text-sm text-slate-800">Everyday app emails</strong><span className="mt-1 block text-xs leading-5 text-slate-500">Notices, confirmations, reminders and receipts.</span>
            </button>
          </div>
          <div className="mt-4 grid gap-3">
            <TextField label="App website" value={website} onChange={value=>{setWebsite(value);resetResult();}} type="url" placeholder="https://app.example.com"
              help={website && !origin ? 'Enter a secure website address beginning with https://' : 'The Gateway automatically recognises the website address. You can paste a full page URL.'}/>
            <TextField label={purpose==='assessment'?'Where should BPA receive new leads?':'Where should we send the test email?'}
              value={testEmail} onChange={value=>{setTestEmail(value);resetResult();}} type="email" placeholder={businessEmail||'team@business.com'}
              help="If blank, the registered business email is used."/>
          </div>
          {!assessmentSupported && <p role="status" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
            <FiAlertCircle className="mr-1 inline"/> This assessment option is configured for the existing Assessment Hub only. New OTP websites must first be registered securely with BPA Verify. No key can bypass that requirement.
          </p>}
        </div>
        {purpose==='general' && <label className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-800">
          <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-blue-600"
            checked={allowCustomerRecipients} disabled={working} onChange={e=>{setAllowCustomerRecipients(e.target.checked);resetResult();}}/>
          <span><strong className="block">Send to different customers</strong>
            <span className="mt-1 block text-xs leading-5 text-slate-600">Enable this for the BPA Applications Hub to send a welcome email to each new customer. Keep the private API key on your application's backend, never in its frontend. If unchecked, only the selected test email is allowed.</span>
          </span>
        </label>}
        <button type="button" disabled={!valid||loading||working||preflightLoading||preflight?.ready===false} onClick={connectAndTest}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-4 text-base font-bold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
          {working?<FiRefreshCw className="animate-spin"/>:<FiZap/>}
          {working ? 'Connecting and testing…' : 'Connect & Test'}
        </button>
        <p className="text-center text-xs text-slate-500">This sends one real test email to your selected inbox. No manual API key entry.</p>
      </div>

      <div className="space-y-4 lg:col-span-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-base font-bold text-slate-900">We'll handle the technical work</h2>
          <div className="mt-4 space-y-3">
            {['Find or register the business and app','Generate or reuse its private key securely','Reuse your current email provider','Set the website and email permissions','Check settings and send one test email'].map(text=><div key={text} className="flex items-start gap-2.5 text-sm text-slate-700"><FiCheckCircle className="mt-0.5 shrink-0 text-blue-600"/>{text}</div>)}
          </div>
          <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-600">
            <FiShield className="mr-1 inline"/> Private API keys stay encrypted on the server. They are not displayed or copied into this website.
          </div>
        </div>
        <div aria-live="polite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><FiZap className="text-blue-600"/> Connection result</h2>
          {!hasRegistered && !phase && <p className="mt-3 text-sm leading-6 text-slate-500">Choose your app and press Connect &amp; Test. The result will appear here, on the same page.</p>}
          {phase && <p className={'mt-3 rounded-xl p-3 text-sm leading-6 '+(fullSuccess?'bg-emerald-50 text-emerald-900':hasRegistered?'bg-blue-50 text-blue-900':'bg-slate-50 text-slate-700')}>{phase}</p>}
          {hasRegistered && <div className="mt-4 space-y-3">
            <StatusLine ok title={'App registered: '+connected.app.name}/>
            <StatusLine ok title="Secure app credential saved on the server"/>
            {diagnostics?.checks?.map(check=><StatusLine key={check.name} ok={check.ok} title={friendlyCheck[check.name] || check.name}/>)}
            {testResult && <StatusLine ok={fullSuccess} title={fullSuccess?'Test email accepted for sending':'Test is not yet accepted'}/>}
            {needsTest && <button type="button" disabled={working} onClick={retryTest} className="flex items-center gap-2 rounded-xl border border-blue-300 px-4 py-2.5 text-sm font-semibold text-blue-700 disabled:opacity-50"><FiRefreshCw/> Check &amp; retry test</button>}
            {fullSuccess && <p className="text-xs leading-5 text-slate-600">Provider acceptance is not proof that the message arrived. Please check your inbox and <Link to="/logs" className="font-bold text-blue-700 underline">Email Logs</Link>.</p>}
            {purpose==='assessment' && <p className="text-xs leading-5 text-slate-600">This test checks email delivery only. Full OTP-to-PDF delivery still needs a real assessment submission, and the PocketHost hooks must be installed.</p>}
            {purpose==='general' && <p className="text-xs leading-5 text-slate-600">Next, connect the application's trusted backend to send event-triggered emails. This setup cannot safely install a private key in an unrelated frontend. <Link to={'/guided-setup?app='+encodeURIComponent(connected.app.id)} className="font-bold text-blue-700 underline">Backend integration guidance</Link>.</p>}
          </div>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <button type="button" aria-expanded={showAdvanced} onClick={()=>setShowAdvanced(x=>!x)} className="flex w-full items-center justify-between gap-2 text-left text-sm font-semibold text-slate-600">
            <span className="flex items-center gap-2"><FiHelpCircle/> Need technical options?</span><FiChevronDown className={showAdvanced?'rotate-180':''}/>
          </button>
          {showAdvanced && <div className="mt-3 space-y-2 text-xs leading-5 text-slate-600">
            <p>Advanced settings include sending limits, templates, secure vault and backend instructions.</p>
            <Link to="/guided-setup" className="block font-bold text-blue-700 underline">Open advanced setup</Link>
            <Link to="/app-config" className="block font-bold text-blue-700 underline">App configuration</Link>
            {connected?.app?.id && <WorkflowRules appId={connected.app.id} clientId={connected.client.id}/>}
          </div>}
        </div>
      </div>
    </section>
  </div>;
}